import React, { useEffect } from 'react';
import { BrowserRouter as Router } from "react-router-dom";
import { registerServiceWorker, requestNotificationPermission } from './utils/pwaUtils';
import { AppProviders } from './app/providers';
import { AppRouter } from './app/router';
import ErrorBoundary from './components/ErrorBoundary';
import DeploymentMaintenanceBanner from './features/notifications/components/DeploymentMaintenanceBanner';
import { useAutoErrorReporter } from './hooks/useAutoErrorReporter';
import "react-toastify/dist/ReactToastify.css";

/** Activates automatic error rate monitoring inside Router context. */
function AutoErrorMonitor() {
  useAutoErrorReporter();
  return null;
}

/**
 * Main App component
 * 
 * This is the root component that:
 * 1. Initializes PWA features (service worker, notifications)
 * 2. Wraps the app with ErrorBoundary for error handling
 * 3. Sets up React Router
 * 4. Provides all context providers via AppProviders
 * 5. Renders the main router with all routes via AppRouter
 * 
 * Architecture:
 * - app/providers/index.jsx - All context providers in correct order
 * - app/router/index.jsx - All route configuration
 * - components/Layout.jsx - Layout wrapper with header, nav, etc.
 */
function App() {
  useEffect(() => {
    const initializePWA = async () => {
      const registration = await registerServiceWorker();
      if (registration) {
        await requestNotificationPermission();
      }
    };

    initializePWA();
  }, []);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister().catch(() => {});
        }
      }).catch(() => {});
    }
  }, []);

  return (
    <ErrorBoundary>
      <DeploymentMaintenanceBanner />
      <Router>
        <AutoErrorMonitor />
        <AppProviders>
          <AppRouter />
        </AppProviders>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
